import { getFormFactor } from './formFactor';

/**
 * Beacon de telemetria de uso. Molde do `clientLog.ts`: fetch nativo (nunca o
 * axios — assim um 401 daqui não passa pelo response interceptor e não abre o
 * SessionExpiredDialog), `keepalive` pra sobreviver ao fechamento da aba, e
 * silêncio total em qualquer falha.
 *
 * **Fila só em memória, de propósito.** Persistir em localStorage faria o
 * backlog do usuário A ser postado depois do login do usuário B — o backend
 * atribui pelo JWT do momento do ENVIO.
 */
const ENDPOINT = '/api/telemetria/eventos';

/** Teto de itens por lote que o backend aceita (`MaxEventosPorLote`). Documentado aqui pra ninguém aumentar o LOTE_FLUSH acima disso. */
export const MAX_FILA = 50;
/** Flush antecipado quando a fila chega neste tamanho. */
export const LOTE_FLUSH = 10;
/** Flush periódico. Folgado dentro do rate limit de 30/min por usuário. */
export const INTERVALO_FLUSH_MS = 20_000;

interface EventoUso { tipo: 'tela'; chave: string; origem: 'mobile' | 'web' }

let fila: EventoUso[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let listenersAtivos = false;
let aoSairHandler: (() => void) | null = null;
let aoMudarVisibilidade: (() => void) | null = null;

function temToken(): boolean {
  try {
    return !!localStorage.getItem('auth_token');
  } catch {
    return false;
  }
}

export function registrarTela(screenId: string): void {
  // Sem token não ENFILEIRA (não basta não enviar): senão o primeiro flush
  // pós-login despejaria eventos de visitante deslogado na conta de quem entrou.
  if (!screenId || !temToken()) return;

  fila.push({ tipo: 'tela', chave: screenId, origem: getFormFactor() });

  // Quem limita a fila é o flush automático abaixo — ele esvazia a cada
  // LOTE_FLUSH eventos, então ela nunca passa disso. Não há cap separado: seria
  // código inalcançável, e código defensivo que nenhum teste consegue exercitar
  // é código que ninguém sabe se funciona.
  if (fila.length >= LOTE_FLUSH) void flushTelemetria();
}

export async function flushTelemetria(): Promise<void> {
  if (fila.length === 0) return;

  // Esvazia ANTES de enviar: o lote é descartado se falhar (não re-enfileira,
  // pra não virar loop) e um flush concorrente não manda o mesmo evento 2×.
  const lote = fila;
  fila = [];

  try {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    await fetch(ENDPOINT, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ eventos: lote }),
    });
  } catch {
    // Telemetria nunca incomoda o usuário. Lote perdido é aceitável.
  }
}

/**
 * Liga o flush periódico e o de saída. Idempotente — o Layout pode montar mais
 * de uma vez (StrictMode) sem duplicar listener nem timer.
 */
export function iniciarTelemetria(): void {
  if (listenersAtivos) return;
  listenersAtivos = true;

  timer = setInterval(() => void flushTelemetria(), INTERVALO_FLUSH_MS);

  // `pagehide` + `visibilitychange` em vez de `beforeunload`: este último é
  // notoriamente ignorado em mobile e com bfcache. Os dois podem disparar em
  // sequência — o flush é idempotente porque esvazia a fila antes de enviar.
  aoSairHandler = () => void flushTelemetria();
  aoMudarVisibilidade = () => {
    if (document.visibilityState === 'hidden') aoSairHandler?.();
  };
  window.addEventListener('pagehide', aoSairHandler);
  document.addEventListener('visibilitychange', aoMudarVisibilidade);
}

/**
 * Descarta o que está pendente. Chamado quando o token troca (logout, sessão
 * expirada, re-login com outra conta): a fila é estado de MÓDULO e o logout é
 * SPA (sem reload), então sem isto os page views de um usuário seriam postados
 * com o token do próximo — o backend atribui pelo JWT do momento do ENVIO.
 */
export function descartarTelemetria(): void {
  fila = [];
}

/** Só pra teste: zera fila, timer e listeners. */
export function __resetTelemetriaParaTeste(): void {
  fila = [];
  if (timer) clearInterval(timer);
  timer = null;
  listenersAtivos = false;

  if (aoSairHandler) {
    window.removeEventListener('pagehide', aoSairHandler);
    aoSairHandler = null;
  }
  if (aoMudarVisibilidade) {
    document.removeEventListener('visibilitychange', aoMudarVisibilidade);
    aoMudarVisibilidade = null;
  }
}

/** Só pra teste. */
export function tamanhoFilaParaTeste(): number {
  return fila.length;
}
